import { Button, Label, Switch } from '@yeshuman/sdk'
import { Trash2 } from 'lucide-react'

import {
  SkillManagerEditableNameButton,
  SkillManagerEditComposer,
  SkillManagerTrailingCluster,
  skillManagerSkillRowClassName,
} from '../skills/skill-manager-parts'

export interface JobSkillDraft {
  skill_id: string
  skill_name: string
  requirement_type: 'required' | 'preferred'
}

function JobSkillRequiredToggle({
  skill,
  disabled,
  onToggleRequired,
}: {
  skill: JobSkillDraft
  disabled?: boolean
  onToggleRequired: (required: boolean) => void
}) {
  const isRequired = skill.requirement_type === 'required'

  return (
    <div className="flex items-center gap-2">
      <Switch
        id={`required-${skill.skill_id}`}
        checked={isRequired}
        disabled={disabled}
        onCheckedChange={onToggleRequired}
        aria-label={`${skill.skill_name} required`}
      />
      <Label htmlFor={`required-${skill.skill_id}`} className="text-xs font-medium text-foreground">
        Required
      </Label>
    </div>
  )
}

export function JobSkillManagerSkillRow({
  skill,
  isEditing,
  editDraft,
  editError,
  editExcludeNames,
  editSuggestionItems,
  suggestionsLoading,
  disabled,
  onStartEdit,
  onEditDraftChange,
  onSaveEdit,
  onCancelEdit,
  onToggleRequired,
  onRemove,
}: {
  skill: JobSkillDraft
  isEditing: boolean
  editDraft: string
  editError: string | null
  editExcludeNames: string[]
  editSuggestionItems: { name: string }[]
  suggestionsLoading?: boolean
  disabled?: boolean
  onStartEdit: () => void
  onEditDraftChange: (next: string) => void
  onSaveEdit: (name: string) => void
  onCancelEdit: () => void
  onToggleRequired: (required: boolean) => void
  onRemove: () => void
}) {
  const trailingControls = (
    <SkillManagerTrailingCluster
      leading={
        <JobSkillRequiredToggle
          skill={skill}
          disabled={disabled}
          onToggleRequired={onToggleRequired}
        />
      }
      actions={
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          disabled={disabled}
          aria-label={`Remove ${skill.skill_name}`}
          title={`Remove ${skill.skill_name}`}
          onClick={onRemove}
        >
          <Trash2 className="size-4 text-muted-foreground" aria-hidden />
        </Button>
      }
    />
  )

  if (isEditing) {
    return (
      <li className={skillManagerSkillRowClassName}>
        <div className="flex items-start justify-between gap-3">
          <SkillManagerEditComposer
            value={editDraft}
            onValueChange={onEditDraftChange}
            excludeNames={editExcludeNames}
            disabled={disabled}
            editError={editError}
            suggestionItems={editSuggestionItems}
            suggestionsLoading={suggestionsLoading}
            enableSuggestions
            onCommit={onSaveEdit}
            onCancel={onCancelEdit}
          />
          {trailingControls}
        </div>
      </li>
    )
  }

  return (
    <li className={skillManagerSkillRowClassName}>
      <div className="flex items-center justify-between gap-3">
        <SkillManagerEditableNameButton
          name={skill.skill_name}
          disabled={disabled}
          onStartEdit={onStartEdit}
          className="flex-1"
        />
        {trailingControls}
      </div>
    </li>
  )
}
